import { expectNumber, expectRecord, expectString } from "./schema";
import { CONTRACT_PRICING_MODES, CONTRACT_STATUSES, SERVICE_RESTRICTION_STATUSES } from "./commercial-contract";
import { parseStudentBillingDate, parseStudentBillingIdentifier, parseStudentBillingServiceMonth, studentBillingEffectivePeriodsOverlap } from "./student-billing";
export const STUDENT_BILLING_COLLECTION_METHODS = ["card", "tenantCollection"];
export const STUDENT_BILLING_COLLECTION_SOURCES = [
    "paymentProcessingDefault",
    "explicitPreference",
    "managementOverride"
];
function enumValue(value, allowed, path) {
    const parsed = expectString(value, path);
    if (!allowed.includes(parsed))
        throw new Error(`${path} is invalid`);
    return parsed;
}
function positiveInteger(value, path) {
    const parsed = expectNumber(value, path);
    if (!Number.isSafeInteger(parsed) || parsed < 1)
        throw new Error(`${path} must be a positive integer`);
    return parsed;
}
function exact(record, keys, path) {
    const unknown = Object.keys(record).filter((key) => !keys.includes(key));
    if (unknown.length)
        throw new Error(`${path} contains unknown fields: ${unknown.sort().join(", ")}`);
}
export function parsePutStudentBillingCollectionPreferenceRequest(value, path = "studentBillingCollectionPreferenceRequest") {
    const record = expectRecord(value, path);
    exact(record, ["payerAssignmentId", "collectionMethod", "effectiveFrom", "effectiveTo", "expectedVersion"], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = record.effectiveTo === null ? null : parseStudentBillingDate(record.effectiveTo, `${path}.effectiveTo`);
    if (effectiveTo !== null && effectiveTo < effectiveFrom)
        throw new Error(`${path}.effectiveTo is before effectiveFrom`);
    return {
        payerAssignmentId: parseStudentBillingIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        collectionMethod: enumValue(record.collectionMethod, STUDENT_BILLING_COLLECTION_METHODS, `${path}.collectionMethod`),
        effectiveFrom,
        effectiveTo,
        expectedVersion: record.expectedVersion === null ? null : positiveInteger(record.expectedVersion, `${path}.expectedVersion`)
    };
}
export function studentBillingServiceMonthAuthorityDate(serviceMonth) {
    return `${parseStudentBillingServiceMonth(serviceMonth, "serviceMonth")}-01`;
}
export function resolveStudentBillingCollectionMethod(input) {
    const authorityDate = parseStudentBillingDate(input.authorityDate, "studentBillingCollectionAuthority.authorityDate");
    const matching = input.preferences.filter((preference) => preference.status === "active"
        && studentBillingEffectivePeriodsOverlap(preference, { effectiveFrom: authorityDate, effectiveTo: authorityDate }));
    if (matching.length > 1)
        throw new Error("STUDENT_BILLING_COLLECTION_PREFERENCE_OVERLAP");
    const preference = matching[0] ?? null;
    if (input.authority.contractPricingMode === "management") {
        return {
            contractPricingMode: input.authority.contractPricingMode,
            collectionMethod: "tenantCollection",
            source: "managementOverride",
            preferenceId: preference?.preferenceId ?? null,
            preferenceVersion: preference?.version ?? null,
            commercialStateVersion: input.authority.commercialStateVersion
        };
    }
    return {
        contractPricingMode: input.authority.contractPricingMode,
        collectionMethod: preference?.collectionMethod ?? "card",
        source: preference ? "explicitPreference" : "paymentProcessingDefault",
        preferenceId: preference?.preferenceId ?? null,
        preferenceVersion: preference?.version ?? null,
        commercialStateVersion: input.authority.commercialStateVersion
    };
}
export function parseStudentBillingAdminCollectionPreferenceDto(value, path = "studentBillingAdminCollectionPreference") {
    const record = expectRecord(value, path);
    exact(record, [
        "contractPricingMode", "contractStatus", "serviceRestrictionStatus", "collectionMethod", "source",
        "effectiveFrom", "effectiveTo", "version", "commercialStateVersion"
    ], path);
    return {
        contractPricingMode: enumValue(record.contractPricingMode, CONTRACT_PRICING_MODES, `${path}.contractPricingMode`),
        contractStatus: enumValue(record.contractStatus, CONTRACT_STATUSES, `${path}.contractStatus`),
        serviceRestrictionStatus: enumValue(record.serviceRestrictionStatus, SERVICE_RESTRICTION_STATUSES, `${path}.serviceRestrictionStatus`),
        collectionMethod: enumValue(record.collectionMethod, STUDENT_BILLING_COLLECTION_METHODS, `${path}.collectionMethod`),
        source: enumValue(record.source, STUDENT_BILLING_COLLECTION_SOURCES, `${path}.source`),
        effectiveFrom: record.effectiveFrom === null ? null : parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`),
        effectiveTo: record.effectiveTo === null ? null : parseStudentBillingDate(record.effectiveTo, `${path}.effectiveTo`),
        version: record.version === null ? null : positiveInteger(record.version, `${path}.version`),
        commercialStateVersion: positiveInteger(record.commercialStateVersion, `${path}.commercialStateVersion`)
    };
}
export function parseStudentBillingCollectionPreference(value, path = "studentBillingCollectionPreference") {
    const record = expectRecord(value, path);
    exact(record, [
        "preferenceId", "tenantId", "studentId", "payerId", "payerAssignmentId", "collectionMethod", "effectiveFrom",
        "effectiveTo", "status", "version", "createdByPlatformUserId", "createdAt", "updatedAt"
    ], path);
    const effectiveFrom = parseStudentBillingDate(record.effectiveFrom, `${path}.effectiveFrom`);
    const effectiveTo = record.effectiveTo === null ? null : parseStudentBillingDate(record.effectiveTo, `${path}.effectiveTo`);
    const status = enumValue(record.status, ["active", "ended"], `${path}.status`);
    if ((effectiveTo !== null && effectiveTo < effectiveFrom) || (status === "ended" && effectiveTo === null)) {
        throw new Error(`${path} has an invalid effective period`);
    }
    return {
        preferenceId: parseStudentBillingIdentifier(record.preferenceId, `${path}.preferenceId`),
        tenantId: parseStudentBillingIdentifier(record.tenantId, `${path}.tenantId`),
        studentId: parseStudentBillingIdentifier(record.studentId, `${path}.studentId`),
        payerId: parseStudentBillingIdentifier(record.payerId, `${path}.payerId`),
        payerAssignmentId: parseStudentBillingIdentifier(record.payerAssignmentId, `${path}.payerAssignmentId`),
        collectionMethod: enumValue(record.collectionMethod, STUDENT_BILLING_COLLECTION_METHODS, `${path}.collectionMethod`),
        effectiveFrom,
        effectiveTo,
        status,
        version: positiveInteger(record.version, `${path}.version`),
        createdByPlatformUserId: parseStudentBillingIdentifier(record.createdByPlatformUserId, `${path}.createdByPlatformUserId`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`)
    };
}
